// ════════════════════════════════════════════════════════════
// commandRegistry — API d'extension de la CommandPalette
// ════════════════════════════════════════════════════════════
// ARCHITECTURE PRÉPARÉE (pas encore remplie). Les modules pourront
// enregistrer leurs commandes contextuelles (« Nouvelle commande
// Mseyha », « Nouvelle dépense », « Rechercher voyage… ») via
// `registerCommand`. Le shell les affiche dans une section dédiée.
//
// Usage (futur, côté module) :
//
//   import { useEffect } from 'react';
//   import { registerCommand } from '@/components/shell';
//   import { Plus } from 'lucide-react';
//
//   useEffect(() =>
//     registerCommand({
//       id: 'mseyha:new',
//       label: 'Nouvelle commande Mseyha',
//       group: 'Mseyha',
//       icon: Plus,
//       keywords: ['voyage', 'gravier', 'livraison'],
//       run: () => navigate({ to: '/mseyha/new' }),
//       // visible: (role) => role !== 'pointeur',  // optionnel
//     }),
//   [navigate]);
//
// `registerCommand` retourne une fonction de désenregistrement
// (à retourner dans le cleanup de l'effet).
// ════════════════════════════════════════════════════════════
import { create } from 'zustand';
import type { LucideIcon } from 'lucide-react';
import type { AppRole } from '@/types/auth';

export interface RegisteredCommand {
  /** Identifiant unique stable (préfixer par module, ex. `mseyha:new`). */
  id: string;
  label: string;
  /** Section affichée dans la palette (défaut : « Actions »). */
  group?: string;
  icon?: LucideIcon;
  /** Termes additionnels pour le fuzzy search. */
  keywords?: string[];
  /** Action exécutée à la sélection (la palette se ferme ensuite). */
  run: () => void;
  /** Filtre de visibilité par rôle (défaut : visible pour tous). */
  visible?: (role: AppRole | null) => boolean;
}

interface CommandRegistryState {
  commands: RegisteredCommand[];
  register: (cmd: RegisteredCommand) => () => void;
}

export const useCommandRegistryStore = create<CommandRegistryState>((set) => ({
  commands: [],
  register: (cmd) => {
    set((s) => ({ commands: [...s.commands.filter((c) => c.id !== cmd.id), cmd] }));
    return () => {
      set((s) => ({ commands: s.commands.filter((c) => c.id !== cmd.id) }));
    };
  },
}));

/**
 * Enregistre une commande dans la palette. Retourne la fonction de
 * désenregistrement (à appeler dans le cleanup d'un `useEffect`).
 */
export function registerCommand(cmd: RegisteredCommand): () => void {
  return useCommandRegistryStore.getState().register(cmd);
}
